/* eslint-disable @stylistic/max-len */
import React, {type RefObject} from 'react';

/**
 * The `EditProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface EditProps {
    /**
     * A string representing the CSS class to be applied to the EditIcon element to override its default styling.
     *
     * @default ''
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     *
     * @default '#0050FF'
     */
    color1?: string;
    /**
     * A number or string representing the height of the EditIcon element.
     *
     * @default 27
     */
    height?: number | string;
    /**
     * The `ref` property represents a reference to the SVG element, allowing for direct manipulation or access to the DOM node.
     */
    ref?: RefObject<SVGSVGElement>;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Edit'
     */
    testId?: string;
    /**
     * A number or string representing the height of the EditIcon element.
     *
     * @default 27
     */
    width?: number | string;
}

/**
 * The `Edit` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `EditProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the EditIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the EditIcon element.
 * @param props.ref       A React ref object that allows access to the underlying SVG element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the EditIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const EditComponent = <Edit className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Edit = ({
    className = '',
    color1 = '#0050FF',
    height = 27,
    ref,
    testId = 'Edit',
    width = 27
}: EditProps) => (
    <svg
        ref={ref}
        className={className}
        data-cy={testId}
        height={height}
        viewBox="0 0 27 27"
        width={width}
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
    >
        <path d="M12.02 2.62a.93.93 0 1 1 0 1.85H3.4A1.54 1.54 0 0 0 1.85 6v17.26a1.54 1.54 0 0 0 1.54 1.55h17.27a1.54 1.54 0 0 0 1.54-1.55v-8.63a.93.93 0 1 1 1.85 0v8.63a3.4 3.4 0 0 1-3.4 3.4H3.4A3.4 3.4 0 0 1 0 23.27V6.01a3.4 3.4 0 0 1 3.4-3.4zM23.12 0a3.54 3.54 0 0 1 2.5 6.04l-11.7 11.72a1 1 0 0 1-.44.24l-4.93 1.24a.92.92 0 0 1-1.12-1.12l1.23-4.94a1 1 0 0 1 .24-.43L20.62 1.04A3.5 3.5 0 0 1 23.12 0m0 1.85c-.44 0-.87.18-1.2.5L10.4 13.87l-.8 3.19 3.2-.8L24.32 4.74a1.7 1.7 0 0 0-1.2-2.89" fill={color1} />
    </svg>
);

Edit.displayName = 'Edit';